import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { useAuth, isAdmin, isClient, isAppraiser } from "../context/AuthContext";
import { api } from "../lib/api";
import { Plus, FolderOpen, Scales, Gavel, ClipboardText, ChartLineUp } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      const c = {};
      try {
        if (isClient(user)) {
          const { data } = await api.get("/jobs?scope=mine");
          c.myJobs = data.length;
          c.openBids = data.filter(j => j.status === "open").length;
        }
        if (isAppraiser(user)) {
          const { data: open } = await api.get("/jobs?scope=open");
          c.openJobs = open.length;
          const { data: mine } = await api.get("/bids/mine");
          c.myBids = mine.length;
          const { data: work } = await api.get("/jobs?scope=assigned");
          c.assigned = work.filter(j => ["awarded","in_progress"].includes(j.status)).length;
          if (user.is_reviewer) {
            const { data: pool } = await api.get("/jobs?scope=review_pool");
            c.reviewPool = pool.length;
          }
        }
        if (isAdmin(user)) {
          const { data: s } = await api.get("/admin/summary");
          c.platform = s.platform_earned;
          c.escrow = s.pending_escrow;
          c.jobs = s.total_jobs;
        }
      } catch (e) { /* ignore */ }
      setCounts(c);
    })();
  }, [user]);

  return (
    <PageLayout>
      <div className="max-w-6xl">
        <div className="kicker">Hello {user?.name?.split(" ")[0]}</div>
        <h1 className="display-serif text-4xl sm:text-5xl mt-2">Your marketplace, at a glance.</h1>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isClient(user) && (
            <>
              <Tile testid="tile-post-job" to="/post-job" Icon={Plus} kicker="Client" title="Post a new job" subtitle="AI-suggested minimum fee before you post" accent />
              <Tile testid="tile-my-jobs" to="/my-jobs" Icon={FolderOpen} kicker="Client" title="My jobs" subtitle={`${counts.myJobs || 0} total • ${counts.openBids || 0} accepting bids`} />
            </>
          )}
          {isAppraiser(user) && (
            <>
              <Tile testid="tile-open-jobs" to="/open-jobs" Icon={Gavel} kicker="Appraiser" title="Open jobs" subtitle={`${counts.openJobs ?? "—"} available to bid`} />
              <Tile testid="tile-my-bids" to="/my-bids" Icon={ClipboardText} kicker="Appraiser" title="My bids" subtitle={`${counts.myBids ?? 0} submitted`} />
              <Tile testid="tile-my-work" to="/my-work" Icon={FolderOpen} kicker="Appraiser" title="My work" subtitle={`${counts.assigned ?? 0} active assignments`} />
              {user.is_reviewer && (
                <Tile testid="tile-review-pool" to="/review-pool" Icon={Scales} kicker="Reviewer" title="Review pool" subtitle={`${counts.reviewPool ?? 0} jobs waiting`} />
              )}
            </>
          )}
          {isAdmin(user) && (
            <>
              <Tile testid="tile-admin" to="/admin" Icon={ChartLineUp} kicker="Admin" title="Platform earnings" subtitle={`$${(counts.platform || 0).toFixed(2)} collected • $${(counts.escrow || 0).toFixed(2)} in escrow`} accent />
            </>
          )}
        </div>

        {!user?.roles?.length && (
          <div className="mt-10 surface-card p-8">
            <div className="display-serif text-2xl">Set up your account</div>
            <p className="text-zinc-600 mt-2">You haven&apos;t chosen a role yet.</p>
            <Link to="/onboarding" className="btn-ink mt-4 inline-flex" data-testid="setup-onboarding-link">Complete setup</Link>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function Tile({ to, Icon, kicker, title, subtitle, accent, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group surface-card p-6 lg:p-8 hover:-translate-y-0.5 transition-transform duration-200 ${accent ? "bg-[#115E59] text-white border-transparent" : ""}`}
    >
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent ? "bg-white/15 text-white" : "bg-[#CCFBF1] text-[#115E59]"}`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className={`kicker mt-4 ${accent ? "text-white/70" : ""}`}>{kicker}</div>
      <div className="display-serif text-2xl mt-1">{title}</div>
      <div className={`text-sm mt-1 ${accent ? "text-white/80" : "text-zinc-600"}`}>{subtitle}</div>
    </Link>
  );
}
